import { EpochTime } from '../../../core/interfaces/IEpochTime';
import { IMessage, Suggestion } from './IMessage';
import { IBusinessReference, IMessageableEntity } from './IMessageableEntity';
import { IStandardResponse } from './IStandardResponse';
import { IFilters } from './IWorkspace';

export interface IChannel {
  type: ChannelType;
  uid: string;
  createdAt: Date;
  displayName: string;
  enabled: boolean;
  isMember: boolean;
  isPrivate: boolean;
  member: IMessageableEntity;
  routing: ChannelServiceType;
  messages: IMessage[];
  features: ChannelFeatures[];
  to?: IMessageableEntity;
  expireAt?: Date | EpochTime;

  // for smart replies
  smartReplies?: Suggestion[];
  isLoadingSmartReplies?: boolean;
  hideSmartReplies?: boolean;

  // for starndar responses
  standardResponses?: IStandardResponse[];
  isLoadingStandardResponses?: boolean;

  // for translations
  lang?: string;
  hideTranslateSetting?: boolean;
  isLoadingChannelLang?: boolean;

  //optionals
  systemTags?: string[];
  consentStatus?: ChannelConsent;
  consent?: { optIn: boolean; optOut?: boolean };
  imageURL?: string;
  lastMessage?: IMessage;
  unreadMessages?: number;
  businessReference?: IBusinessReference;
  isActive?: boolean;
  members?: IMessageableEntity[];
  messagesCompleted?: boolean;
  pinned?: boolean;
  totalMembersCount?: number;
  isVisible?: boolean;
  isMuted?: boolean;
  messagesStartDate?: Date;
  messagesEndDate?: Date;
  unread?: boolean;
  isBotMode?: boolean;
  handOffMode?: string;
  hasOldMessagesToLoad?: boolean;
  messagesTags?: string[];

  // order attributes
  owner?: IChannelOwner;
  awayOwner?: IChannelOwner;
  phoneNumber?: string;
  status?: ChannelStatus;

  isLoadingMessages?: boolean;
  isLoadingOldMessages?: boolean;
}

export interface IChannelCreationDTO {
  type: ChannelType;
  role: ChannelRoles;
  displayName: string;
  isPrivate: boolean;
}

export interface IChannelByParams {
  routing: ChannelServiceType;
  displayName?: string;
  consent?: { optIn: boolean; optOut?: boolean };
  phoneNumber: string;
  orderId?: string;
  ownerDisplayName?: string;
  externalId?: string;
  jobServiceNumber?: string;
  customerId?: string;
  workspace?: string;
  ownerId?: string;
  channelWithoutOrder?: boolean;
  isExternalCreate?: boolean;
  endDateTime?: Date;
  startDateTime?: Date;
  tags?: string[];
  messagesTags?: string[];
  updateInfo?: boolean;
  temporaryDisplayName?: string;
}

// TODO delete when dashboard change
export interface IChannelByPhone extends IChannelByParams {
  extra?: boolean;
}

export interface IChannelOwner {
  id: string;
  displayName: string;
  profileImageURL?: string;
}

export interface IChannelCDK {
  routing: ChannelServiceType;
  phoneNumber: string;
  consent?: { optIn: boolean; optOut?: boolean };
  customerId?: string;
}

export type ChannelListType = 'directChannels' | 'groupChannels' | 'supportChannels' | 'broadcastChannels' | 'allChannels';

export type ChannelServiceType = 'SMS' | 'WHATSAPP' | 'INTERNAL';

export const selectorTypeChannelList: { [key in ChannelType]: ChannelListType } = {
  ALL: 'allChannels',
  ANNOUNCEMENT: 'broadcastChannels',
  DIRECT_MESSAGE: 'directChannels',
  GROUP: 'groupChannels',
  SUPPORT: 'supportChannels',
};

export interface IBulkLoadResponse {
  businessReference: IBusinessReference;
  channel: IChannel;
  createdAt?: Date;
  displayName: string;
  enabled: boolean;
  from: IMessageableEntity;
  lastMessage: IMessage;
  messages: IMessage[];
  muted: boolean;
  pinned: boolean;
  unread: boolean;
  role: ChannelRoles;
  status: string;
  to: IMessageableEntity;
  routing: ChannelServiceType;
  totalCount: number;
  uid: string;
  unreadMessages: number;
  visible: boolean;
  type?: ChannelType;
}
export interface IChannelResponseDTO extends IChannel {
  consent: {
    optIn: boolean;
    optOut: boolean;
  };
  channelUid: string;
}
export interface IChannelFilter {
  filters: IFilters[];
  page: number;
  searchTerm: string;
  size: number;
  orderType?: OrderTypes;
}
export interface IBulkLoadChannelFilters {
  module: string;
  channelUID?: string;
  toPhoneNumber?: string;
  routing?: ChannelServiceType;
  searchTerm?: string;
  exactSearchTerm?: boolean;
  messages?: {
    fromDate?: Date;
    toDate?: Date;
    tags?: string[];
  };
}

export enum ChannelRoles {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
  UNDEFINED = 'UNDEFINED',
  VIEWER = 'VIEWER',
}

export enum ChannelStatus {
  RESPONDED = 'RESPONDED',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  ACTIVE = 'ACTIVE',
}

export enum ChannelType {
  ALL = 'ALL',
  BROADCAST = 'ANNOUNCEMENT',
  DIRECT_MESSAGE = 'DIRECT_MESSAGE',
  GROUP = 'GROUP',
  SUPPORT = 'SUPPORT',
}

export enum OrderTypes {
  REPAIR_ORDER = 'REPAIR_ORDER',
  SALES_ORDER = 'SALES_ORDER',
}
export interface DateChannel {
  nanoseconds: number;
  seconds: number;
}

export enum ChannelFeatures {
  canDeleteMessages = 'canDeleteMessages',
  canSeeDeliveryStatus = 'canSeeDeliveryStatus',
  canSendAttachments = 'canSendAttachments',
  canUseEmoji = 'canUseEmoji',
  canUseGiphy = 'canUseGiphy',
  showPhone = 'showPhone',
  showMessageMemberInfo = 'showMessageMemberInfo',
}

export enum ChannelConsent {
  PENDING = 'PENDING',
  GRANTED = 'GRANTED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  STARTER = 'STARTER',
  REACTIVATE = 'REACTIVATE',
}

export const channelsServices: IChannelsServices = {
  INTERNAL: [
    ChannelFeatures.canDeleteMessages,
    ChannelFeatures.canSeeDeliveryStatus,
    ChannelFeatures.canSendAttachments,
    ChannelFeatures.canUseEmoji,
    ChannelFeatures.canUseGiphy,
    ChannelFeatures.showPhone,
    ChannelFeatures.showMessageMemberInfo,
  ],

  SMS: [
    ChannelFeatures.showPhone,
    ChannelFeatures.canSeeDeliveryStatus,
    ChannelFeatures.canSendAttachments,
  ] as ChannelFeatures[],
  WHATSAPP: [
    ChannelFeatures.showPhone,
    ChannelFeatures.canSendAttachments,
    ChannelFeatures.canUseEmoji,
    ChannelFeatures.canSeeDeliveryStatus,
  ] as ChannelFeatures[],
};

export interface IChannelAllowedFileTypes {
  INTERNAL: { type: string; size: number }[];
  SMS: { type: string; size: number }[];
  WHATSAPP: { type: string; size: number }[];
}

export const ChannelAllowedFileTypes: IChannelAllowedFileTypes = {
  INTERNAL: [],
  SMS: [],
  WHATSAPP: [
    {
      type: ' application/pdf',
      size: 104857600,
    },
    {
      type: ' application/vnd.ms-powerpoint',
      size: 104857600,
    },
    {
      type: ' application/msword',
      size: 104857600,
    },
    {
      type: ' application/vnd.ms-excel',
      size: 104857600,
    },
    {
      type: ' application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      size: 104857600,
    },
    {
      type: ' application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      size: 104857600,
    },
    {
      type: ' application/vnd.openxmlformats-officedocument.presentationml.presentation',
      size: 104857600,
    },
    {
      type: ' audio/aac',
      size: 16777216,
    },
    {
      type: ' audio/mp3',
      size: 16777216,
    },
    {
      type: ' audio/mpeg',
      size: 16777216,
    },
    {
      type: ' audio/amr',
      size: 16777216,
    },
    {
      type: ' video/mp4',
      size: 16777216,
    },
    {
      type: ' image/png',
      size: 5242880,
    },
    {
      type: ' image/jpeg',
      size: 5242880,
    },
  ],
};

export interface IChannelsServices {
  INTERNAL: ChannelFeatures[];
  SMS: ChannelFeatures[];
  WHATSAPP: ChannelFeatures[];
}

export interface ISocketEvent {
  type: string;
  channel: IChannel;
  userEntityUID?: string;
}
